import { createContext, PropsWithChildren, useMemo } from 'react'
import { useCompanyShiftDetail } from 'queries/company/useCompanyShiftDetail'
import { useShiftWork } from 'queries/shift'
import { QueryData } from 'queries/types'
import { sortWorkByPriority } from 'lib/shift'
import { Work } from 'typings/common_defs'
import { useAuthContext } from 'components/auth'

export type ShiftDetail = QueryData<typeof useCompanyShiftDetail>

export interface ShiftDetailContextValues {
  companyId: number
  isOtherCompany: boolean
  shift: ShiftDetail | null
  isLoading: boolean
  isFetching: boolean
  assignedWorkRows: Array<Work>
}

interface ShiftDetailProviderProps extends PropsWithChildren {
  companyId: number
  shiftId: number
}

export const ShiftDetailContext =
  createContext<ShiftDetailContextValues | null>(null)

export const ShiftDetailProvider = (props: ShiftDetailProviderProps) => {
  const { companyId, shiftId, children } = props
  const { company } = useAuthContext()
  // Viewing a shift from a whitelisted company, such as a W2 lead shift
  const isOtherCompany = companyId !== company?.id

  const shiftDetailQuery = useCompanyShiftDetail({ companyId, shiftId })
  const shiftWorkQuery = useShiftWork({ companyId, shiftId })
  const isLoading = shiftDetailQuery.isLoading || shiftWorkQuery.isLoading
  const isFetching = shiftDetailQuery.isFetching || shiftWorkQuery.isFetching

  const shift = useMemo(() => {
    if (!shiftDetailQuery.data || !shiftWorkQuery.data) return null
    return {
      ...shiftDetailQuery.data,
      ...shiftWorkQuery.data,
    } as unknown as ShiftDetail
  }, [shiftDetailQuery.data, shiftWorkQuery.data])

  const assignedWorkRows = useMemo(() => {
    if (!shift) return []
    const numWorkersRequested = shift?.workersNeeded || 0
    const workAssigned = shift?.work ? sortWorkByPriority(shift) : []
    const numWorkAssigned = workAssigned.length

    let rows = workAssigned

    // If we haven't assigned all the workers yet, show skeleton rows
    if (numWorkAssigned < numWorkersRequested) {
      const pendingCount = shift.numWorkersPending || 0

      const skeletonRows: Array<Work> = Array.from(
        Array(numWorkersRequested - numWorkAssigned),
        (_, index) => {
          return {
            id: -index, // id must be a number to not clash with work.id type. We use negative number to differentiate from real work ids
            worker: {
              id: 0,
              profilePicUrl: undefined,
              name: 'skeleton',
            },
            status: index < pendingCount ? 'pending' : undefined,
          }
        }
      )
      rows = [...workAssigned, ...skeletonRows]
    }
    return rows
  }, [shift])

  const values = useMemo(
    () => ({
      companyId,
      isOtherCompany,
      shift,
      isLoading,
      isFetching,
      assignedWorkRows,
    }),
    [companyId, isOtherCompany, isLoading, isFetching, shift, assignedWorkRows]
  )

  return (
    <ShiftDetailContext.Provider value={values}>
      {children}
    </ShiftDetailContext.Provider>
  )
}
